/**
 * Indexes Folder Loader - Loads indexes for a table subfolder
 */

import {
  DatabaseExplorerNode,
  MessageTreeNode,
  IndexesFolderNode,
  createIndexNode,
  IndexNodeMetadata,
} from '../../explorer/models/DatabaseTreeModels';
import {
  createTableSubfolderEmptyNode,
  createTableSubfolderErrorNode,
} from './objectFolderUtils';
import { getIndexes, IndexInfo, formatIndexDisplayName } from './indexesService';
import { getEngineForConnection } from './tableExpansion';

export interface LoadIndexesFolderOptions {
  node: IndexesFolderNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  forceReload?: boolean;
}

/**
 * Loads the indexes for a table when the Indexes folder is expanded
 */
export async function loadIndexesFolder({
  node,
  updateNode,
  forceReload = false,
}: LoadIndexesFolderOptions) {
  if (!forceReload && node.children && node.children.length > 0) {
    const firstChild = node.children[0];
    if (firstChild && !['message', 'error'].includes(firstChild.type)) {
      return;
    }
  }

  const { connectionId, database, schema, tableName } = node.metadata;

  if (!database) {
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, '', schema, tableName, 'indexes-folder', node.id, 'Database not specified'
        ),
      ],
      isLoading: false,
    });
    return;
  }

  updateNode(node.id, {
    children: [
      {
        id: `loading-${connectionId}-${database}-${schema}-${tableName}-indexes`,
        parentId: node.id,
        name: 'Loading indexes...',
        type: 'message',
        canHaveChildren: false,
        children: [],
        isExpanded: false,
        icon: 'loading',
        metadata: { connectionId, database },
      } as MessageTreeNode,
    ],
    isLoading: true,
  });

  try {
    const engine = getEngineForConnection(connectionId);
    const result = await getIndexes(connectionId, database, schema, tableName, engine);

    if (result.error) {
      updateNode(node.id, {
        children: [
          createTableSubfolderErrorNode(
            connectionId, database, schema, tableName, 'indexes-folder', node.id, result.error
          ),
        ],
        isLoading: false,
      });
      return;
    }

    if (result.indexes.length === 0) {
      updateNode(node.id, {
        children: [
          createTableSubfolderEmptyNode(
            connectionId, database, schema, tableName, 'indexes-folder', node.id
          ),
        ],
        isLoading: false,
      });
      return;
    }

    const indexNodes = result.indexes.map((index: IndexInfo) => {
      const displayName = formatIndexDisplayName(index);
      const metadata: IndexNodeMetadata = {
        connectionId,
        database,
        schema,
        tableName,
        indexName: index.name,
        columns: index.columns,
        isUnique: index.isUnique,
        indexType: index.indexType,
      };
      const indexNode = createIndexNode(metadata, node.id);
      indexNode.name = displayName;
      return indexNode;
    });

    updateNode(node.id, {
      children: indexNodes,
      isLoading: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load indexes';
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, database, schema, tableName, 'indexes-folder', node.id, message
        ),
      ],
      isLoading: false,
    });
  }
}
